import { useQuery } from "@tanstack/react-query";
import { fetchWithError } from "src/react-query/fetchWithError";
import { queryKeys } from "src/react-query/queryKeys";
import { selector } from "src/react-query/select";
import { is } from "src/util/is";

export const useTransactions = ({
  teamId,
  page,
  limit,
  filters,
  search,
  sort,
  order,
}) => {
  const url = new URL(window.location.href);
  const params = url.searchParams;

  // Instead of switching query keys for charger/tag/payout,
  // build filters that can work with pagination
  let filterConfig = filters ? JSON.parse(filters) : null;

  // Handle charger filter from URL
  if (params.has("charger")) {
    const chargerFilter = {
      field: "chargepoint_id",
      operator: "equals",
      value: params.get("charger"),
    };
    if (filterConfig) {
      filterConfig.filters = [...(filterConfig.filters || []), chargerFilter];
    } else {
      filterConfig = {
        logic: "ALL",
        filters: [chargerFilter],
      };
    }
  }

  // Handle tag filter from URL
  if (params.has("tag")) {
    const tagFilter = {
      field: "id_tag",
      operator: "equals",
      value: params.get("tag"),
    };
    if (filterConfig) {
      filterConfig.filters =[...(filterConfig.filters || []), tagFilter];
    } else {
      filterConfig = {
        logic: "ALL",
        filters: [tagFilter],
      };
    }
  }

  // Rebuild serialized filters
  const serializedFilters = filterConfig ? JSON.stringify(filterConfig) : undefined;

  // Prepare params for backend (pagination + filters + search + sort)
  const paginationParams =
    page && limit
      ? {
          page,
          limit,
          ...(serializedFilters ? { filters: serializedFilters } : {}),
          ...(search ? { search } : {}),
          ...(sort ? { sort } : {}),
          ...(order ? { order } : {}),
        }
      : serializedFilters
      ? { filters: serializedFilters }
      : undefined;

  // Always use the paginated transactions endpoint
  let keys = queryKeys.transactions(teamId, paginationParams);

  // Keep special handling for payoutId and customer transactions as they need specific endpoints
  if (params.has("payoutId"))
    keys = queryKeys.payoutTransactions(teamId, params.get("payoutId"));

  if (params.has("email") || params.has("phone")) {
    keys = queryKeys.customerTransactions(teamId, {
      id: params.get("id"),
      email: params.get("email"),
      phone: encodeURIComponent(params.get("phone")),
      domain: params.get("domain"),
      ocpp_tag_pk: params.get("ocpp_tag_pk"),
    });
  }

  return useQuery(keys, fetchWithError, {
    enabled: !is.empty(teamId),
    select: (data) => {
      // If pagination params were used (implying we expect a paginated response)
      // and the response has pagination metadata, return the formatted object.
      if (page && limit && data?.pagination) {
        return {
          data: data.data,
          total: data.pagination.total,
        };
      }
      // Fallback for non-paginated or error cases
      return selector(data);
    },
    keepPreviousData: !!(page && limit), // Keep data while fetching new page
  });
};

export const useTransactionsId = ({ teamId, transactionId }) =>
  useQuery(queryKeys.transaction(teamId, transactionId), fetchWithError, {
    enabled: !is.empty(teamId) && !is.empty(transactionId),
    select: selector,
  });

export const useUpiTransactions = ({
  teamId,
  dateRange = "today",
  page,
  limit,
  enabled = true,
}) => {
  // Prepare pagination params
  const paginationParams = page && limit ? { page, limit } : undefined;

  return useQuery(
    queryKeys.upiTransactions(teamId, dateRange, paginationParams),
    fetchWithError,
    {
      enabled: !is.empty(teamId) && enabled,
      select: (data) => {
        // If pagination params were used and we have pagination data in response
        if (page && limit && data?.data?.total !== undefined) {
          // Note: The structure might be data.data.transactions based on controller
          // The controller returns { data: { transactions: [], total: ... } }
          // fetchWithError returns 'data' which is the response body.
          // So 'data' here is { success: true, message: ..., data: { transactions: ..., total: ... } }

          // Wait, fetchApi returns 'data' from axios response.
          // selector returns data?.data.

          // Let's stick to the pattern used in useTransactions
          // But we need to be careful about the specific response structure of this endpoint

          // If the endpoint returns { data: { transactions: [], total: ... } }
          // Then fetchWithError returns this entire object.

          const responseData = data?.data;
          if (responseData && responseData.transactions) {
            return {
              data: responseData.transactions,
              total: responseData.total || responseData.transactions.length,
            };
          }
        }
        return selector(data);
      },
      staleTime: 0, // Always refetch when dateRange changes
      keepPreviousData: !!(page && limit),
    }
  );
};
